import { z } from "zod";

/**
 * Contrat du module Bobines de la console (spec du 27/09/2026 et son amendement du 28/09/2026,
 * issue #220, ADR 0012).
 *
 * Ce fichier vit sous `src/app/` parce que `src/index.ts` appartient au template : backend et
 * frontend le lisent par leur pont `src/app/contract.ts`. L'agent du PC (`packages/bobines`,
 * Python) ecrit le meme JSON : **aucun champ ne se renomme ici sans lui**. Les contraintes CHECK
 * de `migrations/1003_bobines.sql` disent la meme chose que ces schemas (cle, couleur,
 * identifiant de bobine, minimum, reserve, raisons d'echec, catalogue) : un test du backend les
 * compare.
 */

/** Seuils des états (spec, section 3), à un seul endroit. */
export const SEUILS_BOBINES = { neuvePct: 95, presqueVideG: 150 } as const;
/** Au-delà, le bandeau de la page vire au jaune (spec, section 4). */
export const AGE_ALERTE_JOURS = 3;
/** Le jeton Bambu vit environ 90 jours ; la page prévient 10 jours avant (porte 0 : pas de renouvellement). */
export const PREAVIS_JETON_JOURS = 10;

export const etatBobineSchema = z.enum(["neuve", "entamee", "presque_vide", "finie"]);
export type EtatBobine = z.infer<typeof etatBobineSchema>;

export const raisonEchecSchema = z.enum([
  "jeton_bambu_absent",
  "jeton_bambu_refuse",
  "api_bambu_refuse",
  "api_bambu_format",
  "reseau_bambu",
  "autre",
]);
export type RaisonEchec = z.infer<typeof raisonEchecSchema>;

/** Une date ISO 8601 (`toISOString`, ou `datetime.isoformat()` côté Python). */
const horodatage = z.string().min(20).max(40);
/**
 * Aucun caractère de contrôle (C0 et DEL). Écrit une fois, pour une seule exception à Biome :
 * sa règle `noControlCharactersInRegex` refuse ces caractères dans un motif, et c'est justement
 * ce que ce motif refuse dans le texte.
 */
// biome-ignore lint/suspicious/noControlCharactersInRegex: le motif refuse les caractères de contrôle.
const SANS_CONTROLE = /^[^\u0000-\u001f\u007f]*$/;
/** Du texte d'une ligne, sans caractère de contrôle. */
const ligne = (max: number) => z.string().min(1).max(max).regex(SANS_CONTROLE);
/** Six chiffres hexadécimaux en minuscules, sans `#`. */
const couleurHex = z.string().regex(/^[0-9a-f]{6}$/);
/** Une empreinte SHA-256 en hexadécimal : celle d'une copie, celle du catalogue. */
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);

/**
 * Signale chaque valeur deja vue plus haut dans la liste, a son rang : l'agent sait laquelle
 * corriger. Sert aux identifiants de la copie et aux cles du catalogue.
 */
function signalerDoublons(
  ctx: z.RefinementCtx,
  liste: string,
  valeurs: readonly string[],
  champ: string,
  message: string,
): void {
  const vues = new Set<string>();
  for (const [i, valeur] of valeurs.entries()) {
    if (vues.has(valeur)) ctx.addIssue({ code: "custom", path: [liste, i, champ], message });
    vues.add(valeur);
  }
}

/** Clé d'une référence : trois morceaux en minuscules ASCII, marque.gamme.couleur. */
export const CLE_REFERENCE = /^[a-z0-9-]{1,40}\.[a-z0-9-]{1,60}\.[a-z0-9-]{1,40}$/;
export const cleReferenceSchema = z.string().regex(CLE_REFERENCE);

export const MINIMUM_MAX = 20;
export const minimumSchema = z.number().int().min(0).max(MINIMUM_MAX);

/**
 * La réserve d'une couleur (amendement du 28/09/2026) : ses bobines neuves rangées sur
 * l'étagère, que le Filament Manager ne connaît pas tant qu'elles ne sont pas passées par l'AMS.
 * Nicolas la monte à la réception d'une commande ; elle baisse toute seule quand une bobine
 * neuve de cette couleur apparaît dans une copie.
 */
export const RESERVE_MAX = 99;
export const reserveSchema = z.number().int().min(0).max(RESERVE_MAX);

// ---------------------------------------------------------------------------------------
// Le PC envoie : routes `/api/bobines/machine/*`, jeton BOBINES_TOKEN.

export const bobineCopieSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9._:-]+$/),
  cle: cleReferenceSchema,
  marque: ligne(60),
  gamme: ligne(60),
  matiere: ligne(30),
  couleur_nom: ligne(60),
  couleur_hex: couleurHex,
  restant_g: z.number().min(0).max(5000),
  depart_g: z.number().positive().max(5000).nullable(),
  emplacement: ligne(60).nullable(),
  maj_le: horodatage.nullable(),
});
export type BobineCopie = z.infer<typeof bobineCopieSchema>;

export const MAX_BOBINES = 500;
export const copieSchema = z
  .object({
    lue_le: horodatage,
    agent: ligne(20),
    jeton_expire_le: horodatage.nullable(),
    vide_assumee: z.boolean().default(false),
    bobines: z.array(bobineCopieSchema).max(MAX_BOBINES),
  })
  .superRefine((copie, ctx) => {
    signalerDoublons(
      ctx,
      "bobines",
      copie.bobines.map((b) => b.id),
      "id",
      "identifiant en double",
    );
  });
export type Copie = z.infer<typeof copieSchema>;

export const reponseCopieSchema = z.object({
  nouvelle: z.boolean(),
  bobines: z.number().int().nonnegative(),
  /**
   * L'empreinte du catalogue que la console garde, telle que l'agent l'a calculee ; nulle tant
   * qu'aucun catalogue n'est arrive. L'agent n'envoie le sien que si elle differe de la sienne.
   */
  catalogue_empreinte: sha256.nullable(),
});
export type ReponseCopie = z.infer<typeof reponseCopieSchema>;

export const etatAgentSchema = z.object({
  agent: ligne(20),
  raison: raisonEchecSchema,
  detail: z.string().max(300).regex(SANS_CONTROLE).nullable(),
  jeton_expire_le: horodatage.nullable(),
});
export type EtatAgent = z.infer<typeof etatAgentSchema>;

export const pingBobinesSchema = z.object({ module: z.literal("bobines"), ok: z.literal(true) });

/**
 * Le catalogue des couleurs de Bambu Studio (amendement du 28/09/2026), lu sur le PC dans
 * `filaments_color_codes.json` : de quoi mettre en réserve une couleur jamais chargée dans
 * l'AMS. La clé est calculée par l'agent comme celle d'une bobine : une couleur choisie ici et
 * la même bobine lue plus tard par l'AMS ont la même clé.
 */
export const MAX_COULEURS_CATALOGUE = 1000;
export const couleurCatalogueSchema = z.object({
  cle: cleReferenceSchema,
  marque: ligne(60),
  gamme: ligne(60),
  couleur_nom: ligne(60),
  couleur_hex: couleurHex,
  code_produit: z
    .string()
    .regex(/^[0-9A-Za-z-]{1,20}$/)
    .nullable(),
});
export type CouleurCatalogue = z.infer<typeof couleurCatalogueSchema>;

export const catalogueSchema = z
  .object({
    agent: ligne(20),
    source: z.literal("bambu-studio"),
    /** Calculée par l'agent (SHA-256 des couleurs triées par clé), gardée telle quelle. */
    empreinte: sha256,
    couleurs: z.array(couleurCatalogueSchema).min(1).max(MAX_COULEURS_CATALOGUE),
  })
  .superRefine((catalogue, ctx) => {
    signalerDoublons(
      ctx,
      "couleurs",
      catalogue.couleurs.map((c) => c.cle),
      "cle",
      "cle en double",
    );
  });
export type Catalogue = z.infer<typeof catalogueSchema>;

/** `nouveau` : le catalogue a remplacé celui d'avant ; `couleurs` : combien la console en garde. */
export const reponseCatalogueSchema = z.object({
  nouveau: z.boolean(),
  couleurs: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------------------
// Nicolas lit et règle : `GET /api/bobines`, `GET /api/bobines/catalogue`,
// `PUT /api/bobines/minimum`, `PUT /api/bobines/reserve`.

export const bobineVueSchema = z.object({
  id: z.string(),
  restant_g: z.number(),
  depart_g: z.number().nullable(),
  etat: etatBobineSchema,
  emplacement: z.string().nullable(),
  maj_le: z.string().nullable(),
});
export const referenceVueSchema = z.object({
  cle: z.string(),
  marque: z.string(),
  gamme: z.string(),
  couleur_nom: z.string(),
  couleur_hex: z.string(),
  minimum: minimumSchema,
  /** Les neuves de la copie, celles que l'AMS a lues. */
  neuves: z.number().int().nonnegative(),
  /** Les neuves de l'étagère : le rachat les compte avec celles de la copie. */
  reserve: z.number().int().nonnegative(),
  a_racheter: z.number().int().nonnegative(),
  restant_g: z.number().int().nonnegative(),
  etats: z.object({
    neuve: z.number().int().nonnegative(),
    entamee: z.number().int().nonnegative(),
    presque_vide: z.number().int().nonnegative(),
    finie: z.number().int().nonnegative(),
  }),
  bobines: z.array(bobineVueSchema),
});
export type ReferenceVue = z.infer<typeof referenceVueSchema>;
export const gammeVueSchema = z.object({
  marque: z.string(),
  gamme: z.string(),
  bobines: z.number().int().nonnegative(),
  references: z.array(referenceVueSchema),
});
export type GammeVue = z.infer<typeof gammeVueSchema>;
export const ligneCoursesSchema = z.object({
  cle: z.string(),
  couleur_nom: z.string(),
  couleur_hex: z.string(),
  neuves: z.number().int().nonnegative(),
  reserve: z.number().int().nonnegative(),
  minimum: minimumSchema,
  a_racheter: z.number().int().positive(),
  sans_bobine: z.boolean(),
});
export const groupeCoursesSchema = z.object({
  marque: z.string(),
  gamme: z.string(),
  lignes: z.array(ligneCoursesSchema),
});
export const signeVueSchema = z.object({
  le: z.string(),
  ok: z.boolean(),
  raison: raisonEchecSchema.nullable(),
  detail: z.string().nullable(),
  echec_depuis: z.string().nullable(),
});
export const vueBobinesSchema = z.object({
  copie: z
    .object({
      lue_le: z.string(),
      recue_le: z.string(),
      vue_le: z.string(),
      nb_bobines: z.number().int(),
    })
    .nullable(),
  signe: signeVueSchema.nullable(),
  jeton_expire_le: z.string().nullable(),
  gammes: z.array(gammeVueSchema),
  courses: z.array(groupeCoursesSchema),
  total_a_racheter: z.number().int().nonnegative(),
  seuils: z.object({ neuve_pct: z.number(), presque_vide_g: z.number() }),
});
export type VueBobines = z.infer<typeof vueBobinesSchema>;

export const demandeMinimumSchema = z.object({ cle: cleReferenceSchema, minimum: minimumSchema });
export type DemandeMinimum = z.infer<typeof demandeMinimumSchema>;

/**
 * `PUT /api/bobines/reserve` : une ecriture conditionnelle. `avant` est la reserve que la page
 * affichait (0 pour une couleur sans ligne, ajoutee depuis le catalogue) : la console n'ecrit
 * que si la base vaut encore `avant`, sinon 409 `reserve_changee`. Sans elle, une page ouverte
 * avant qu'une copie ait fait baisser la reserve (une bobine neuve arrivee dans l'AMS) renverrait
 * l'ancienne valeur, et le decompte serait perdu sans que personne le voie. Le minimum, que rien
 * ne change dans le dos de la page, garde son ecriture absolue.
 */
export const demandeReserveSchema = z.object({
  cle: cleReferenceSchema,
  reserve: reserveSchema,
  avant: reserveSchema,
});
export type DemandeReserve = z.infer<typeof demandeReserveSchema>;
/** La reserve enregistree. */
export const reponseReserveSchema = z.object({ cle: cleReferenceSchema, reserve: reserveSchema });
export type ReponseReserve = z.infer<typeof reponseReserveSchema>;

/**
 * `GET /api/bobines/catalogue` : le catalogue groupé par gamme, gammes et couleurs triées par
 * nom en français. `recu_le` nul et aucune gamme tant que l'agent n'a rien envoyé.
 */
export const couleurCatalogueVueSchema = z.object({
  cle: z.string(),
  couleur_nom: z.string(),
  couleur_hex: z.string(),
  code_produit: z.string().nullable(),
});
export const gammeCatalogueVueSchema = z.object({
  marque: z.string(),
  gamme: z.string(),
  couleurs: z.array(couleurCatalogueVueSchema),
});
export const vueCatalogueSchema = z.object({
  recu_le: z.string().nullable(),
  gammes: z.array(gammeCatalogueVueSchema),
});
export type VueCatalogue = z.infer<typeof vueCatalogueSchema>;
